import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Badge, Button, Dialog, Input, Select, StatusPip, Table } from '../components/ui';
import { ConfirmDialog, EmptyStateBlock, ErrorState, LoadingState } from '../components/states';
import { useToast } from '../app/ToastProvider';
import { useSession } from '../app/session';
import {
  claimDevice,
  listDevices,
  regroupDevice,
  revokeDevice,
  sendCommand,
  subscribeDevices,
  type DeviceRecord
} from '../api/devices';
import { listScreens, type ScreenDto } from '../api/screens';
import { fetchTenantUsage, type TenantUsage } from '../api/tenant';
import { DeviceDetail } from '../features/devices/DeviceDetail';
import { deviceClassMark, lockdownLabel, relativeTime, statusPresentation } from '../features/devices/deviceView';

type SortKey = 'name' | 'status' | 'last_seen';

export function Devices() {
  const { session } = useSession();
  const { notify, notifyError } = useToast();
  const role = session?.role ?? 'viewer';
  const writable = role !== 'viewer';

  const [devices, setDevices] = useState<DeviceRecord[]>([]);
  const [screens, setScreens] = useState<ScreenDto[]>([]);
  const [usage, setUsage] = useState<TenantUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sort, setSort] = useState<SortKey>('last_seen');
  const [selected, setSelected] = useState<DeviceRecord | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showClaim, setShowClaim] = useState(false);
  const [showRegroup, setShowRegroup] = useState(false);
  const [confirmBulkRevoke, setConfirmBulkRevoke] = useState(false);

  const load = useCallback(async () => {
    try {
      const [rows, screenRows] = await Promise.all([listDevices(), listScreens().catch(() => [])]);
      setDevices(rows);
      setScreens(screenRows);
      setError('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not load devices.');
    } finally {
      setLoading(false);
    }
  }, []);

  // FB-0.2 / CC-8: usage against the plan limits ("2 of 5 devices"). Best
  // effort — the table still loads if the usage endpoint is unavailable.
  useEffect(() => {
    let active = true;
    fetchTenantUsage()
      .then((next) => active && setUsage(next))
      .catch(() => active && setUsage(null));
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => subscribeDevices(setDevices), []);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = devices.filter((device) => {
      const matchesQuery =
        !needle ||
        (device.name ?? '').toLowerCase().includes(needle) ||
        (device.group ?? '').toLowerCase().includes(needle) ||
        device.id.toLowerCase().includes(needle);
      const matchesStatus = statusFilter === 'all' || device.status === statusFilter;
      return matchesQuery && matchesStatus;
    });
    return filtered.sort((a, b) => {
      if (sort === 'name') return (a.name ?? '').localeCompare(b.name ?? '');
      if (sort === 'status') return (a.status ?? '').localeCompare(b.status ?? '');
      return Date.parse(b.last_seen ?? '') - Date.parse(a.last_seen ?? '');
    });
  }, [devices, query, statusFilter, sort]);

  const toggle = (id: string) =>
    setSelectedIds((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));

  async function bulkReload() {
    await Promise.all(selectedIds.map((id) => sendCommand(id, 'reload').catch(() => {})));
    notify(`Reload queued for ${selectedIds.length} device(s)`);
    setSelectedIds([]);
  }

  async function bulkRegroup(group: string) {
    await Promise.all(selectedIds.map((id) => regroupDevice(id, group).catch(() => {})));
    notify(`Group set for ${selectedIds.length} device(s)`);
    setSelectedIds([]);
    setShowRegroup(false);
    await load();
  }

  async function bulkRevoke() {
    for (const id of selectedIds) await revokeDevice(id).catch(() => {});
    notify(`${selectedIds.length} device(s) revoked`);
    setSelectedIds([]);
    await load();
  }

  return (
    <main className="workspace-main">
      <header className="page-header">
        <div>
          <span className="index-label">CONTROL ROOM / DEVICES</span>
          <h1>Devices</h1>
        </div>
        {writable && (
          <Button type="button" onClick={() => setShowClaim(true)}>
            Claim device
          </Button>
        )}
      </header>

      <div className="toolbar">
        <Input
          aria-label="Search devices"
          placeholder="Search name or group"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <Select aria-label="Filter by status" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
          <option value="all">All statuses</option>
          <option value="online">Online</option>
          <option value="offline">Offline</option>
          <option value="pairing">Pairing</option>
          <option value="revoked">Revoked</option>
        </Select>
        <Select aria-label="Sort devices" value={sort} onChange={(event) => setSort(event.target.value as SortKey)}>
          <option value="last_seen">Last seen</option>
          <option value="name">Name</option>
          <option value="status">Status</option>
        </Select>
        {usage && (
          <span className="detail-note" data-usage>
            {usage.devices.used} of {usage.devices.max} devices
          </span>
        )}
      </div>

      {writable && selectedIds.length > 0 && (
        <div className="bulk-bar">
          <span>{selectedIds.length} selected</span>
          <Button variant="quiet" type="button" onClick={() => void bulkReload()}>
            Reload
          </Button>
          <Button variant="quiet" type="button" onClick={() => setShowRegroup(true)}>
            Regroup
          </Button>
          <Button variant="danger" type="button" onClick={() => setConfirmBulkRevoke(true)}>
            Revoke
          </Button>
        </div>
      )}

      {loading && <LoadingState label="Loading devices" />}
      {error && <ErrorState title="Devices unavailable" detail={error} onRetry={() => void load()} />}

      {!loading && !error && visible.length === 0 && (
        <EmptyStateBlock title="No devices" detail="Claim a device to get started." />
      )}

      {!loading && !error && visible.length > 0 && (
        <section className="panel">
          <Table>
            <thead>
              <tr>
                {writable && <th aria-label="Select" />}
                <th>Device</th>
                <th>Group</th>
                <th>Status</th>
                <th>Last seen</th>
                <th>Screen</th>
                <th>Lockdown</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((device) => {
                const status = statusPresentation(device.status);
                // CC-6: names, not ids. The screen a device shows and the content
                // playing on it are named; the raw record id sits under Details.
                const screen = screens.find((row) => row.id === (device as { screen?: string }).screen);
                return (
                  <tr key={device.id} onClick={() => setSelected(device)} className="row-clickable">
                    {writable && (
                      <td onClick={(event) => event.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label={`Select ${device.name}`}
                          checked={selectedIds.includes(device.id)}
                          onChange={() => toggle(device.id)}
                        />
                      </td>
                    )}
                    <td>
                      <strong>
                        <Badge className="class-mark">{deviceClassMark(device.capabilities?.device_class)}</Badge>{' '}
                        {device.name ?? 'Unnamed'}
                      </strong>
                      <details className="row-details" onClick={(event) => event.stopPropagation()}>
                        <summary>Details</summary>
                        <span className="mono">{device.id}</span>
                      </details>
                    </td>
                    <td>{device.group ?? '—'}</td>
                    <td>
                      <StatusPip status={status.pip} label={status.label} />
                    </td>
                    <td className="mono">{relativeTime(device.last_seen)}</td>
                    <td>
                      {screen ? (
                        <>
                          {screen.name}
                          <small className="row-subtitle">
                            {screen.schedule
                              ? 'Runs its schedule'
                              : screen.default_playlist
                                ? 'Playlist'
                                : 'No content yet'}
                          </small>
                        </>
                      ) : (
                        <span className="detail-note">{(device as { screen?: string }).screen ? '—' : 'No screen'}</span>
                      )}
                    </td>
                    <td>{lockdownLabel(device.capabilities?.lockdown)}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </section>
      )}

      <DeviceDetail
        device={selected}
        role={role}
        onClose={() => setSelected(null)}
        onChanged={() => void load()}
      />

      <ClaimDialog
        open={showClaim}
        onClose={() => setShowClaim(false)}
        onClaimed={() => {
          notify('Device claimed');
          void load();
        }}
      />

      <RegroupDialog
        open={showRegroup}
        onClose={() => setShowRegroup(false)}
        onSubmit={(group) => void bulkRegroup(group)}
      />

      <ConfirmDialog
        open={confirmBulkRevoke}
        title="Revoke devices"
        confirmWord="revoke"
        detail={`${selectedIds.length} device(s) will lose their tokens and return to pairing.`}
        confirmLabel="Revoke"
        onClose={() => setConfirmBulkRevoke(false)}
        onConfirm={() => {
          setConfirmBulkRevoke(false);
          void bulkRevoke();
        }}
      />
    </main>
  );
}

type ClaimDialogProps = {
  open: boolean;
  onClose: () => void;
  onClaimed: () => void;
};

function ClaimDialog({ open, onClose, onClaimed }: ClaimDialogProps) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [group, setGroup] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setCode('');
      setName('');
      setGroup('');
      setError('');
    }
  }, [open]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await claimDevice(code.trim(), name.trim() || 'New device', group.trim() || undefined);
      onClaimed();
      onClose();
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : 'Claim failed.';
      setError(
        /expired/i.test(message)
          ? 'That pairing code has expired. Show a fresh code on the device.'
          : message
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} title="Claim device" onClose={onClose}>
      {error && (
        <p className="dialog-alert" role="alert">
          <span aria-hidden="true">▲</span>
          {error}
        </p>
      )}
      <form onSubmit={submit} className="auth-form">
        <label className="index-label" htmlFor="pair-code">
          PAIRING CODE
        </label>
        <Input id="pair-code" value={code} onChange={(event) => setCode(event.target.value.toUpperCase())} required />
        <p className="detail-note">The device shows this code while it waits to be claimed. It expires after 10 minutes.</p>
        <label className="index-label" htmlFor="claim-name">
          NAME
        </label>
        <Input id="claim-name" value={name} onChange={(event) => setName(event.target.value)} />
        <label className="index-label" htmlFor="claim-group">
          GROUP
        </label>
        <Input id="claim-group" value={group} onChange={(event) => setGroup(event.target.value)} />
        <div className="dialog-actions">
          <Button variant="quiet" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? 'Claiming…' : 'Claim'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function RegroupDialog({
  open,
  onClose,
  onSubmit
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (group: string) => void;
}) {
  const [group, setGroup] = useState('');
  useEffect(() => {
    if (open) setGroup('');
  }, [open]);

  return (
    <Dialog open={open} title="Regroup devices" onClose={onClose}>
      <label className="index-label" htmlFor="bulk-group">
        GROUP
      </label>
      <Input id="bulk-group" value={group} onChange={(event) => setGroup(event.target.value)} />
      <div className="dialog-actions">
        <Button variant="quiet" type="button" onClick={onClose}>
          Cancel
        </Button>
        <Button type="button" disabled={!group.trim()} onClick={() => onSubmit(group.trim())}>
          Apply
        </Button>
      </div>
    </Dialog>
  );
}
